import React, { useEffect, useRef, useState } from "react";
import Reveal from "./Reveal";

const STEPS = [
  {
    n: "01",
    title: "Análisis inicial del caso",
    body: "Revisión de la situación de la embarcación, deuda, titularidad y documentación disponible.",
  },
  {
    n: "02",
    title: "Definición de la vía adecuada",
    body: "Valoración jurídica y técnica para determinar si corresponde venta, retirada, achatarramiento o reclamación.",
  },
  {
    n: "03",
    title: "Gestión del procedimiento",
    body: "Coordinación legal, pericial y operativa hasta la resolución del caso.",
  },
  {
    n: "04",
    title: "Recuperación del espacio",
    body: "El objetivo final es liberar el amarre y reducir el impacto económico y operativo para el puerto o marina.",
  },
];

const Process = () => {
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const winH = window.innerHeight;
      // start when section enters the lower half, finish when it exits the upper half
      const startY = winH * 0.85;
      const endY = winH * 0.25;
      const total = startY - endY;
      const traveled = startY - rect.top;
      let p = traveled / total;
      p = Math.max(0, Math.min(1, p));
      setProgress(p);
      setActiveStep(Math.min(STEPS.length - 1, Math.floor(p * STEPS.length + 0.001)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section
      id="como-trabajamos"
      data-testid="section-process"
      className="py-24 sm:py-32 bg-white relative overflow-hidden"
    >
      {/* Decorative watermark */}
      <div className="absolute right-0 top-12 font-display text-[14rem] lg:text-[18rem] leading-none text-navy/[0.025] select-none pointer-events-none -mr-8">
        04
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-sky" />
              <div className="text-xs uppercase tracking-[0.28em] text-sky font-semibold">
                04 — Cómo trabajamos
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.1] tracking-tight">
              Un proceso <span className="italic text-navy/70">claro</span>, en cuatro fases.
            </h2>
          </Reveal>
        </div>

        <div ref={trackRef} className="mt-20 relative">
          {/* Animated horizontal progress line (desktop) */}
          <div className="hidden lg:block absolute top-[58px] left-0 right-0 h-px bg-slate-200" />
          <div
            className="hidden lg:block absolute top-[58px] left-0 h-[2px] bg-gradient-to-r from-sky to-navy transition-[width] duration-300 ease-out"
            style={{ width: `${progress * 100}%` }}
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
            {STEPS.map((step, idx) => {
              const isActive = idx <= activeStep;
              return (
                <Reveal key={step.n} delay={idx * 100}>
                  <div
                    data-testid={`process-step-${idx}`}
                    className="relative group"
                  >
                    {/* Node dot */}
                    <div className="hidden lg:flex relative items-center justify-center w-5 h-5 rounded-full ring-4 ring-white">
                      <div
                        className={`w-5 h-5 rounded-full transition-all duration-500 ${
                          isActive
                            ? "bg-sky scale-100 shadow-[0_0_0_4px_rgba(43,176,241,0.18)]"
                            : "bg-slate-300 scale-75"
                        }`}
                      />
                    </div>

                    <div
                      className={`font-display text-6xl lg:text-7xl leading-none pt-8 lg:pt-10 transition-all duration-700 ${
                        isActive ? "text-navy" : "text-navy/15"
                      }`}
                    >
                      {step.n}
                    </div>

                    <div className="mt-5 flex items-center gap-3">
                      <div
                        className={`h-px transition-all duration-700 ${
                          isActive ? "w-8 bg-sky" : "w-4 bg-slate-300"
                        }`}
                      />
                      <div className="text-[10px] uppercase tracking-[0.25em] text-ink-soft font-semibold">
                        Fase 0{idx + 1}
                      </div>
                    </div>

                    <h3 className="mt-3 font-display text-xl lg:text-2xl text-navy leading-snug">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-ink-muted text-[15px] leading-relaxed pr-4">
                      {step.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
